/**
 * JourneyTrackBand — the authored Journey track as the full-width S-curve
 * step band (anchor rttf-drop-hub.html:2158 — "Journey · 7 stops",
 * STATEMENT/BAND footprint).
 *
 * The curve is `MechanicJourney` (the real component, previously reachable
 * only through `DropSectionRenderer` — gap-register row 9); this widget is
 * only the data adapter. The stops are the JOURNEY's segments — the authored
 * track from the Journey Editor — NOT competition data bent into shape:
 * a stop per distinct `order` (branch alternates deliberately share an
 * order and collapse into one stop, the same rule `useOnboardingCards`
 * records), done when any alternate at that order is complete, active at
 * the first stop not yet done.
 *
 * Signed-out viewers see the full track with nothing done and stop 1 active
 * — the road ahead is the recruiting surface, not a login wall.
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { QuestDisplay } from "@devour/client";
import { useGetJourneysForUser } from "@/hooks/useGetJourneysForUser";
import { useGetQuestProgressionForUser } from "@/hooks/useGetQuestProgressionForUser";
import { MechanicJourney, type JourneyStep } from "./mechanics/MechanicJourney";
import "./JourneyTrackBand.scss";

interface JourneyTrackBandProps {
    /** Specific journey to render; absent = the surface's first. */
    journeyId?: string;
    /**
     * Which authoring surface's journeys to read. Defaults to PLAY_PAGE —
     * the surface RTTF's track is authored against; no Drop-specific value
     * exists in QuestDisplay yet.
     */
    surface?: QuestDisplay;
    sectionTitle?: string;
}

export function JourneyTrackBand({ journeyId, surface, sectionTitle }: JourneyTrackBandProps) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    const { data: journeys } = useGetJourneysForUser(fullToken, {
        surface: surface ?? QuestDisplay.PLAY_PAGE,
    });
    const journey = journeyId
        ? journeys?.find(j => j.id === journeyId)
        : journeys?.[0];

    const questIds = useMemo(
        () => (journey?.segments ?? []).map(s => s.questConfigId).filter(Boolean),
        [journey],
    );
    const { data: progressions } = useGetQuestProgressionForUser(fullToken, questIds);

    const steps: JourneyStep[] = useMemo(() => {
        const segments = journey?.segments ?? [];
        if (segments.length === 0) return [];

        /* One stop per distinct order; alternates share the stop. */
        const byOrder = new Map<number, typeof segments>();
        for (const segment of segments) {
            const group = byOrder.get(segment.order) ?? [];
            group.push(segment);
            byOrder.set(segment.order, group);
        }
        const orders = [...byOrder.keys()].sort((a, b) => a - b);

        const doneAt = (order: number): boolean =>
            (byOrder.get(order) ?? []).some(segment =>
                progressions?.some(p => p.questConfigId === segment.questConfigId && p.isComplete));

        const firstOpen = orders.find(order => !doneAt(order));

        return orders.map(order => {
            const group = byOrder.get(order) ?? [];
            return {
                id: String(order),
                label: group[0]?.groupLabel ?? group[0]?.name ?? `Stop ${order}`,
                done: doneAt(order),
                active: order === firstOpen,
            };
        });
    }, [journey, progressions]);

    if (!journey || steps.length === 0) return null;

    const doneCount = steps.filter(s => s.done).length;

    return (
        <div className="journey-band">
            <div className="journey-band__head">
                <span className="journey-band__kicker">{sectionTitle || "The Journey"}</span>
                <h3 className="journey-band__title">{journey.name}</h3>
            </div>
            <MechanicJourney
                variant="marquee"
                steps={steps}
                statusLine={`Stop ${Math.min(doneCount + 1, steps.length)} of ${steps.length}`}
            />
        </div>
    );
}
